import { Link } from "react-router-dom";
import { ArrowUpRight, TrendingUp } from "lucide-react";
import { useT, useImg } from "@/context/ContentContext";
import PageHead from "@/components/PageHead";

const STEPS = [
    { n: "01", t: "Ouvir", d: "Percebemos o que pretende, as suas prioridades e aquilo que espera da intervenção.", extra: "Ouvir bem antes de propor. Cada obra começa com uma conversa, não com um catálogo." },
    { n: "02", t: "Analisar", d: "Visitamos o imóvel, avaliamos os trabalhos e procuramos identificar condicionantes antes de começar.", extra: "É durante a análise que evitamos boa parte dos imprevistos que aparecem noutras obras a meio do caminho." },
    { n: "03", t: "Orçamentar", d: "Apresentamos uma proposta clara, com trabalhos, condições e garantias. Consulta-a e adjudica-a na sua Área do Cliente.", extra: "Orçamento discriminado, sem letra miúda. Se o trabalho mudar, explicamos antes de avançar." },
    { n: "04", t: "Preparar", d: "Planeamos materiais, especialidades e sequência dos trabalhos — e indicamos-lhe a data prevista de execução.", extra: "Uma obra bem preparada é uma obra sem sobressaltos. Cronograma, materiais e especialidades organizados antes de começar." },
    { n: "05", t: "Executar", d: "Executamos e coordenamos os trabalhos. Recebe atualizações com fotografias: o que fizemos e o que acontece a seguir.", extra: "Um resumo por email no fim de cada semana de obra em que existam atualizações. Proteção do imóvel e respeito pelos vizinhos e pelos restantes profissionais." },
    { n: "06", t: "Entregar", d: "Verificamos os trabalhos, tratamos dos acabamentos finais e entregamos a obra. O histórico fica sempre disponível.", extra: "Vistoria detalhada, acabamentos verificados, limpeza e entrega. Bem feito. Ponto." },
];

export default function ComoTrabalhamos() {
    const t = useT();
    const img = useImg();
    const heroImg = img("como.image", "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920");
    return (
        <>
            <PageHead
                title="Como trabalhamos — O Homem da Obra"
                description="Um método claro em seis etapas — do primeiro contacto à entrega. Ouvir, analisar, orçamentar, preparar, executar e entregar."
                canonical="https://ohomemdaobra.pt/como-trabalhamos"
            />

            {/* HERO — full-bleed image with overlay text, like Home */}
            <section className="relative pt-24 pb-8 sm:pb-14">
                <div className="max-w-[1400px] mx-auto px-3 sm:px-4">
                    <div className="relative rounded-[2rem] overflow-hidden min-h-[70vh] bg-[hsl(var(--forest))]">
                        <img src={heroImg} alt="Como trabalhamos" className="absolute inset-0 w-full h-full object-cover opacity-60" data-testid="como-hero-image" />
                        <div className="absolute inset-0 bg-gradient-to-b from-[hsl(var(--forest))]/40 via-[hsl(var(--forest))]/45 to-[hsl(var(--forest))]/85" />
                        <div className="relative min-h-[70vh] flex flex-col justify-end p-6 sm:p-10 lg:p-16">
                        <div className="self-start">
                            <div className="eyebrow-light mb-6"><TrendingUp className="w-3.5 h-3.5" /> {t("como.eyebrow", "Como trabalhamos")}</div>
                        </div>
                        <h1 className="font-display text-white text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.02] max-w-4xl">
                                {t("como.title", "Obras sem dores de cabeça não é apenas uma frase")}<span className="text-[hsl(var(--copper))]">.</span>
                            </h1>
                            <p className="text-white/85 text-lg leading-relaxed mt-8 max-w-2xl">
                                {t("como.subtitle", "Um método claro em seis etapas, pensado para reduzir imprevistos. Em cada uma, sabe onde estamos e o que vem a seguir.")}
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* 6 STEPS */}
            <section className="py-16 sm:py-24">
                <div className="max-w-5xl mx-auto px-6 sm:px-10 space-y-4">
                    {STEPS.map((s, i) => (
                        <div key={s.n} className={`card-stone p-6 sm:p-8 ${i % 2 === 0 ? "sm:ml-0 sm:mr-8" : "sm:ml-8 sm:mr-0"}`} data-testid={`como-step-${s.n}`}>
                            <div className="grid sm:grid-cols-[auto_1fr] gap-6">
                                <div className="font-mono text-xl text-[hsl(var(--copper))]">{s.n}</div>
                                <div>
                                    <h3 className="font-display text-2xl font-bold mb-2">{s.t}</h3>
                                    <p className="text-[hsl(var(--graphite))]/75 mb-3">{s.d}</p>
                                    <p className="text-sm text-[hsl(var(--graphite))]/60 italic border-l-2 border-[hsl(var(--copper))] pl-4">{s.extra}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* EXTRA — trabalhos adicionais */}
            <section className="py-16 bg-white">
                <div className="max-w-3xl mx-auto px-6 sm:px-10 text-center">
                    <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight leading-[1.05] mb-6">
                        {t("como.extra.title", "E quando surgem trabalhos adicionais?")}
                    </h2>
                    <p className="text-[hsl(var(--graphite))]/75 text-lg leading-relaxed">
                        {t("como.extra.text", "Uma remodelação pode revelar situações que não eram visíveis antes de começar. Se durante a obra surgir algo que altere o trabalho previsto, explicamos primeiro — e mostramos o que encontrámos antes de avançar.")}
                    </p>
                </div>
            </section>

            <section className="pb-20">
                <div className="max-w-[1400px] mx-auto px-3 sm:px-4">
                    <div className="bg-[hsl(var(--forest))] rounded-[2rem] p-10 sm:p-16 text-center text-white">
                        <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight max-w-2xl mx-auto">
                            {t("como.cta.title", "Vamos falar sobre a sua obra?")}
                        </h2>
                        <Link to="/orcamento" className="btn-copper mt-8">
                            Fale-nos da sua obra
                            <span className="arrow-circle"><ArrowUpRight className="w-3.5 h-3.5" /></span>
                        </Link>
                    </div>
                </div>
            </section>
        </>
    );
}
